import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trash2, RotateCcw, Archive } from "lucide-react";
import { toast } from "sonner";
import { eurf } from "./shared";

export default function OrgDetail({ oid, token, onBack, onDelete }) {
  const [d, setD] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/superadmin/orgs/${oid}/detail`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setD(data)).catch(() => {});
  }, [oid, token]);
  useEffect(() => { load(); }, [load]);

  const restore = async () => {
    setRestoring(true);
    try {
      const { data } = await axios.post(`${API}/superadmin/orgs/${oid}/restore-archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Données restaurées (${data.restored_documents} élément${data.restored_documents > 1 ? "s" : ""}) — organisation repassée en pause.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la restauration");
    } finally {
      setRestoring(false);
    }
  };

  if (!d) return <p className="text-zinc-500">Chargement…</p>;
  const o = d.org, b = d.billing;
  const info = (l, v) => (<div><p className="text-xs text-zinc-500">{l}</p><p className="text-sm text-white">{v || "—"}</p></div>);
  return (
    <div className="space-y-4" data-testid="sa-org-detail">
      <button data-testid="sa-detail-back" onClick={onBack} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"><ArrowLeft className="h-4 w-4" />Retour à la liste</button>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {o.logo ? <img src={o.logo} alt="" className="h-12 w-12 rounded-xl object-cover" /> : <div className="flex h-12 w-12 items-center justify-center rounded-xl text-lg font-bold text-white" style={{ background: o.brand_color || "#FF6A00" }}>{(o.name || "?")[0]}</div>}
          <div><h1 className="font-display text-2xl font-bold text-white">{o.name}</h1><p className="text-xs text-zinc-500">Inscrit le {o.created_at ? new Date(o.created_at).toLocaleDateString("fr-FR") : "—"}</p></div>
        </div>
        <Button data-testid="sa-detail-delete" onClick={() => onDelete && onDelete({ id: oid, name: o.name })} variant="outline" className="rounded-full border-red-500/40 text-red-400 hover:bg-red-500/10"><Trash2 className="mr-1.5 h-4 w-4" />Supprimer</Button>
      </div>
      {b.data_archived && (
        <div data-testid="sa-org-archived-banner" className="flex flex-col gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2.5">
            <Archive className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm font-semibold text-amber-300">Données archivées</p>
              <p className="text-xs text-amber-200/70">Archivées le {b.archived_at ? new Date(b.archived_at).toLocaleDateString("fr-FR") : "—"} après inactivité prolongée. Elles sont conservées et peuvent être restaurées.</p>
            </div>
          </div>
          <Button data-testid="sa-org-restore-btn" onClick={restore} disabled={restoring} className="shrink-0 rounded-full bg-amber-500 text-black hover:bg-amber-400">
            <RotateCcw className="mr-1.5 h-4 w-4" />{restoring ? "Restauration…" : "Restaurer les données"}
          </Button>
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-3 rounded-2xl border border-zinc-800 bg-zinc-950 p-4">
          <p className="text-sm font-semibold text-white">Coordonnées & légal</p>
          {info("Email", o.contact_email)}{info("Téléphone", o.phone)}{info("Adresse", [o.address, o.city].filter(Boolean).join(", "))}
          {info("SIRET", o.siret)}{info("N° TVA", o.vat_number)}{info("Statut fiscal", o.fiscal_status)}
        </div>
        <div className="space-y-3 rounded-2xl border border-zinc-800 bg-zinc-950 p-4">
          <p className="text-sm font-semibold text-white">Abonnement & paiement</p>
          {info("Plan", b.plan || "—")}{info("Statut", b.data_archived ? "archivé" : b.subscription_status)}{info("Montant/mois", eurf(b.price))}
          {info("Commission", b.commission_pct != null ? b.commission_pct + " %" : "—")}{info("Connect", o.connect_status)}
          {info("Vitrine", o.store_slug ? `/boutique/${o.store_slug}` : "Non publiée")}
        </div>
        <div className="space-y-3 rounded-2xl border border-zinc-800 bg-zinc-950 p-4">
          <p className="text-sm font-semibold text-white">Activité</p>
          {info("CA confirmé", eurf(d.revenue))}{info("Devis", d.counts.quotes)}{info("Contacts", d.counts.contacts)}{info("Événements", d.counts.events)}{info("Lieux", d.counts.venues)}
          {info("Utilisateurs", d.users.map((u) => u.email).join(", "))}
        </div>
      </div>
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4">
        <p className="mb-3 text-sm font-semibold text-white">Catalogue ({d.catalog.length})</p>
        {d.catalog.length === 0 ? <p className="text-sm text-zinc-500">Aucun article.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="text-xs uppercase text-zinc-500"><tr><th className="py-2 pr-3">Article</th><th className="py-2 pr-3">Catégorie</th><th className="py-2 pr-3">Prix</th><th className="py-2 pr-3">Vitrine</th></tr></thead>
            <tbody>{d.catalog.map((c) => (<tr key={c.id} className="border-t border-zinc-800/60"><td className="py-2 pr-3 text-white">{c.name}</td><td className="py-2 pr-3 text-zinc-400">{c.category || c.module || "—"}</td><td className="py-2 pr-3">{eurf(c.price)}</td><td className="py-2 pr-3">{c.store_visible !== false ? <span className="text-emerald-400">oui</span> : <span className="text-zinc-500">non</span>}</td></tr>))}</tbody></table></div>
        )}
      </div>
    </div>
  );
}
